import type * as React from 'react'
import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ChevronDownIcon,
  CoachMark,
  DropdownMenu,
  Input,
  NewBadge,
  SearchIcon,
  Tooltip,
} from '@buffer-mono/popcorn'

import { FilterByChannelItem } from '~publish/components/FilterByChannel/components/FilterByChannelItem'
import { useChannels } from '~publish/components/PublishSidebar/useChannels'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'

import useMatchPostingGoal from '../../hooks/useMatchPostingGoal'
import type { ScheduleV2 } from '../../hooks/usePostingSchedule'
import type { PostingScheduleStrategyInput } from '../../hooks/usePostingSchedulePreview'
import { PostingTimePreviewModal } from '../PostingTimePreviewModal/PostingTimePreviewModal'

import styles from './GeneratePostingTimes.module.css'

const SPOTLIGHT_STYLE: React.CSSProperties & Record<`--${string}`, string> = {
  '--spotlight-left': '0',
  '--spotlight-top': '5px',
}
const COACH_MARK_STYLE: React.CSSProperties & Record<`--${string}`, string> = {
  '--coach-mark-max-width': '360px',
}

export function GeneratePostingTimes(): JSX.Element {
  const channelData = useSelectedChannel()
  const { id: channelId } = channelData ?? {
    id: undefined,
  }

  // States for the posting schedule preview
  const [strategy, setStrategy] = useState<PostingScheduleStrategyInput>({
    optimize: { postsPerWeek: 3, updateMode: 'override' },
  })

  // States for alert modals
  const [showPostingSchedulePreviewModal, setShowPostingSchedulePreviewModal] =
    useState(false)

  // Check for values needed for the dropdown menu
  const { counts, loading: loadingCounts } = usePostCounts({
    organizationId: channelData?.organizationId,
    channelIds: channelId ? [channelId] : undefined,
  })
  const hasQueuedPosts = !loadingCounts && (counts?.queue ?? 0) > 0
  const channels = useChannels()
  const channelsToCopyFrom = channels.channels.filter(
    (channel) =>
      channel.id !== channelId &&
      !isEmptyPostingSchedule(channel.postingSchedule ?? []),
  )
  const channelsToCopyFromDisabled = channelsToCopyFrom.length === 0
  const channelsToCopyFromDisabledMessage = channelsToCopyFromDisabled
    ? 'No channels to copy from'
    : undefined

  // Handle search filtering
  const [searchValue, setSearchValue] = useState('')
  const filteredChannelsToCopyFrom = useMemo(() => {
    if (searchValue.length > 1) {
      return channelsToCopyFrom.filter(
        (channel) =>
          channel.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          channel.displayName
            ?.toLowerCase()
            .includes(searchValue.toLowerCase()) ||
          channel.service.toLowerCase().includes(searchValue.toLowerCase()),
      )
    }
    return channelsToCopyFrom
  }, [searchValue, channelsToCopyFrom])

  const {
    isDisabled: matchPostingGoalDisabled,
    disabledMessage: matchPostingGoalDisabledMessage,
    goal,
    activeSlotCount,
  } = useMatchPostingGoal()

  // Click handler for the DropdownMenu items
  const onClickMatchPostingGoal = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    event.preventDefault()
    if (matchPostingGoalDisabled) {
      return
    }
    setStrategy({
      optimize: {
        updateMode: 'add',
        postsPerWeek: goal,
      },
    })
    setShowPostingSchedulePreviewModal(true)
  }

  const onClickUseRecommendedTimes = (): void => {
    setStrategy({
      optimize: {
        updateMode: 'override',
        postsPerWeek:
          activeSlotCount > 0 ? activeSlotCount : goal > 0 ? goal : 3,
      },
    })
    setShowPostingSchedulePreviewModal(true)
  }

  const [sourceChannelName, setSourceChannelName] = useState<
    string | undefined
  >()

  const onClickCopyFromChannel = (id: string, name: string): void => {
    setStrategy({
      copyFromChannel: id,
    })
    setSourceChannelName(name)
    setShowPostingSchedulePreviewModal(true)
  }

  return (
    <>
      <PostingTimePreviewModal
        open={showPostingSchedulePreviewModal}
        onOpenChange={setShowPostingSchedulePreviewModal}
        channel={channelData}
        strategy={strategy}
        hasQueuedPosts={hasQueuedPosts}
        sourceChannelName={sourceChannelName}
      />

      <FeatureCoachMark>
        <DropdownMenu>
          <DropdownMenu.Trigger>
            <Button variant="secondary">
              Generate New Posting Slots <ChevronDownIcon />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <ConditionalTooltip
              tooltipContent={matchPostingGoalDisabledMessage}
            >
              <DropdownMenu.Item
                disabled={matchPostingGoalDisabled}
                onClick={onClickMatchPostingGoal}
              >
                Meet My Posting Goal
              </DropdownMenu.Item>
            </ConditionalTooltip>
            <DropdownMenu.Item onClick={onClickUseRecommendedTimes}>
              Update Slots to Match Buffer&apos;s Recommendations
            </DropdownMenu.Item>
            <DropdownMenu.Sub>
              <ConditionalTooltip
                tooltipContent={channelsToCopyFromDisabledMessage}
              >
                <DropdownMenu.SubTrigger disabled={channelsToCopyFromDisabled}>
                  Copy from Channel
                </DropdownMenu.SubTrigger>
              </ConditionalTooltip>
              <DropdownMenu.SubMenu>
                {channelsToCopyFrom.length > 8 && (
                  <Input
                    className={styles.searchInput}
                    placeholder="Search channels"
                    prefix={<SearchIcon />}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setSearchValue(e.currentTarget.value)
                      }
                    }}
                  />
                )}
                {filteredChannelsToCopyFrom.length === 0 && (
                  <DropdownMenu.Item disabled>
                    No channels found
                  </DropdownMenu.Item>
                )}
                <div className={styles.channelsToCopyFromSubMenu}>
                  {filteredChannelsToCopyFrom.map((channel) => (
                    <DropdownMenu.Item
                      key={channel.id}
                      onClick={() =>
                        onClickCopyFromChannel(channel.id, channel.name)
                      }
                    >
                      <FilterByChannelItem
                        locationData={channel.locationData}
                        serverUrl={channel.serverUrl}
                        avatar={channel.avatar}
                        name={channel.name}
                        service={channel.service}
                      />
                    </DropdownMenu.Item>
                  ))}
                </div>
              </DropdownMenu.SubMenu>
            </DropdownMenu.Sub>
          </DropdownMenu.Content>
        </DropdownMenu>
      </FeatureCoachMark>
    </>
  )
}

function isEmptyPostingSchedule(postingSchedule: ScheduleV2[]): boolean {
  return postingSchedule.every(({ times }) => times.length === 0) ?? true
}

function FeatureCoachMark({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const banner = useDismissableBanner('match-posting-times-coachmark', {
    trackDismiss: true,
  })
  if (!banner.isActive) return <>{children}</>
  return (
    <CoachMark>
      <CoachMark.Overlay>
        {children}
        <CoachMark.Spotlight style={SPOTLIGHT_STYLE} />
      </CoachMark.Overlay>
      <CoachMark.Content side="left" style={COACH_MARK_STYLE}>
        <CoachMark.Title>
          <NewBadge /> Match Posting Slots With Your Goal
        </CoachMark.Title>
        <CoachMark.Description>
          We can add recommended times based on Buffer data to match your goal.
          Existing posting times and scheduled posts won&apos;t be affected.
        </CoachMark.Description>
        <CoachMark.Footer>
          <CoachMark.Dismiss>
            <Button variant="primary" size="small" onClick={banner.dismiss}>
              Got It
            </Button>
          </CoachMark.Dismiss>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}

const ConditionalTooltip = ({
  children,
  tooltipContent,
}: {
  children: React.ReactNode
  tooltipContent?: string
}): JSX.Element => {
  if (!tooltipContent) {
    return <>{children}</>
  }
  return (
    <Tooltip align="start" delay={0} content={tooltipContent}>
      {children}
    </Tooltip>
  )
}
